import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { GameAPI, LobbyManagerState } from "@rosette/ts/src/business/api/GameAPI.js";
import type { BotType } from "@rosette/ts/src/business/game/BotType.js";
import type { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";


export class LobbyManagerStateResult {
    readonly data: LobbyManagerState | undefined;
    readonly loading: boolean;
    readonly error: Error | null;

    constructor(data: LobbyManagerState | undefined, loading: boolean, error: Error | null) {
        this.data = data;
        this.loading = loading;
        this.error = error;
    }

    isBotAvailable(botType: BotType, ruleset: Ruleset): boolean {
        if (!this.data)
            return false;

        return this.data.availableBots.some(bot =>
            bot.getType() === botType && bot.getSettings() === ruleset,
        );
    }
}


export function useLobbyManagerState(gameAPI: GameAPI): LobbyManagerStateResult {
    const { data, isLoading, error } = useQuery({
        queryKey: ["lobbyManagerState"],
        queryFn: () => gameAPI.getLobbyManagerState(),
        staleTime: 3 * 1000,
        refetchInterval: 3 * 1000,
    });

    return useMemo(() => new LobbyManagerStateResult(
        data,
        isLoading,
        error,
    ), [data, isLoading, error]);
}
